import { useCallback, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { RegionPrompt } from "@/components/customer/RegionPrompt";
import {
  Banknote,
  Bell,
  CreditCard,
  Shield,
  User,
  LogOut,
  RefreshCw,
  Building2,
  ChevronDown,
} from "lucide-react";
import { platformApi } from "@/platform/api";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { useCustomer } from "@/contexts/CustomerContext";

export function CustomerLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    profile,
    kycApproved,
    kycStatus,
    pendingApplications,
    loans,
    handleSignOut,
    fetchData,
    isLoading,
    premiumUnlocked,
  } = useCustomer();

  const currentPath = location.pathname.replace("/dashboard", "") || "/";

  const activeLoansCount = loans.filter((l) => l.status === "active").length;

  // Unread badge. Refreshed on navigation and whenever the notifications page
  // changes something, so the count never lags behind what the user just did.
  const [unread, setUnread] = useState(0);
  const refreshUnread = useCallback(() => {
    platformApi.unreadCount()
      .then((r) => setUnread(r.unread))
      .catch(() => undefined);
  }, []);

  useEffect(() => { refreshUnread(); }, [refreshUnread, location.pathname]);
  useEffect(() => {
    window.addEventListener("notifications:changed", refreshUnread);
    return () => window.removeEventListener("notifications:changed", refreshUnread);
  }, [refreshUnread]);

  const navItems = [
    {
      title: "Borrow",
      icon: Banknote,
      path: "/",
      visible: true,
    },
    {
      title: "My Loans",
      icon: CreditCard,
      path: "/loans",
      visible: true,
      badge: activeLoansCount || undefined,
    },
    {
      title: "Notifications",
      icon: Bell,
      path: "/notifications",
      visible: true,
      badge: unread || undefined,
    },
    {
      title: "KYC",
      icon: Shield,
      path: "/kyc",
      visible: true,
      badge: kycApproved ? undefined : 1,
    },
    {
      title: "Account",
      icon: User,
      path: "/account",
      visible: true,
    },
  ];

  const isActive = (path: string) => {
    if (path === "/") return currentPath === "/" || currentPath === "";
    return currentPath.startsWith(path);
  };

  return (
    <SidebarProvider>
      <Sidebar variant="inset" collapsible="icon">
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" tooltip="Ngwees Capital">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-foreground text-background">
                  <Building2 className="h-4 w-4" />
                </div>
                <div className="flex flex-col gap-0.5 leading-none">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    Ngwees Capital
                  </span>
                  <span className="font-semibold">
                    {profile?.full_name || "Client"}
                  </span>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        <SidebarSeparator />

        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel>Menu</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {navItems
                  .filter((item) => item.visible)
                  .map((item) => (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton
                        tooltip={item.title}
                        isActive={isActive(item.path)}
                        onClick={() =>
                          navigate(
                            item.path === "/"
                              ? "/dashboard"
                              : `/dashboard${item.path}`
                          )
                        }
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                      {typeof item.badge === "number" && item.badge > 0 && (
                        <SidebarMenuBadge>{item.badge}</SidebarMenuBadge>
                      )}
                    </SidebarMenuItem>
                  ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <Badge
                className={
                  premiumUnlocked
                    ? "mx-2 mb-2 bg-success/10 text-success"
                    : "mx-2 mb-2 bg-muted text-muted-foreground"
                }
              >
                {premiumUnlocked ? "Premium" : "Standard"} plan
              </Badge>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Refresh" onClick={() => fetchData()}>
                <RefreshCw
                  className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                />
                <span>Refresh</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Sign out" onClick={handleSignOut}>
                <LogOut className="h-4 w-4" />
                <span>Sign out</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>

        <SidebarRail />
      </Sidebar>

      <SidebarInset>
        <header className="flex h-14 items-center gap-2 border-b px-6">
          <SidebarTrigger className="-ml-2" />
          <div className="flex-1" />
          <Badge
            variant="outline"
            className={
              kycApproved
                ? "border-success/30 text-success"
                : "border-warning/30 text-warning"
            }
          >
            KYC: {kycStatus || "not submitted"}
          </Badge>
        </header>

        <div className="flex-1 overflow-auto p-6">
          <Outlet />
        </div>
        <RegionPrompt />
      </SidebarInset>
    </SidebarProvider>
  );
}
